import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import Navbar from "@/components/Navbar";
import { PartyPopper, Loader2, Download, Mail, ArrowRight, ExternalLink, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Confetti from "@/components/Confetti";

export default function PlanCongrats() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const load = async () => {
    try {
      const [p, o] = await Promise.all([
        api.get(`/plans/${id}`),
        api.get(`/plans/${id}/onboarding`),
      ]);
      setPlan(p.data);
      setOnboarding(o.data);
    } catch {
      toast.error("Kunne ikke laste planen");
      navigate("/dashboard");
    }
  };

  useEffect(() => { load(); }, [id]);

  const toggleStep = async (step_key, done) => {
    setOnboarding((o) => o ? { ...o, steps: o.steps.map((s) => s.key === step_key ? { ...s, done } : s) } : o);
    try {
      const { data } = await api.patch(`/plans/${id}/onboarding`, { step_key, done });
      setOnboarding(data);
    } catch {
      toast.error("Kunne ikke lagre");
      load();
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/plans/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "planiq.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 402 ? "Planen er ikke låst opp enda" : "Nedlasting feilet");
    } finally {
      setDownloading(false);
    }
  };

  const emailPdf = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/plans/${id}/email-pdf`);
      setEmailSent(true);
      toast.success(`Sendt til ${data.to}`);
    } catch (e) {
      const msg = e.response?.status === 503
        ? "E-postsending er ikke aktivert enda"
        : (e.response?.data?.detail || "Sending feilet");
      toast.error(msg);
    } finally {
      setEmailing(false);
    }
  };

  if (!plan || !onboarding) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const doneCount = onboarding.steps.filter((s) => s.done).length;
  const total = onboarding.steps.length;
  const pct = Math.round((doneCount / total) * 100);
  const title = plan.inputs?.business_name || plan.title || "din bedrift";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <Confetti />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <Link to={`/plan/${id}`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8" data-testid="congrats-back">
          <ArrowLeft className="h-4 w-4" /> Til planen
        </Link>

        <Card className="bg-slate-900 text-white border-0 rounded-3xl overflow-hidden relative">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
          <CardContent className="p-10 sm:p-14 relative">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]" data-testid="congrats-badge">
              <PartyPopper className="h-3.5 w-3.5" /> Forretningsplanen din er klar
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl font-medium tracking-tight" data-testid="congrats-title">
              🎉 Gratulerer, {title}!
            </h1>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-xl">
              Første steg er tatt. Last ned PDF-en, send en kopi til e-posten din, og bruk sjekklisten under for å komme i gang som gründer.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={downloadPdf}
                disabled={downloading}
                size="lg"
                className="rounded-full h-12 px-7 bg-white text-slate-900 hover:bg-slate-100"
                data-testid="congrats-pdf-btn"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Last ned PDF</>}
              </Button>
              <Button
                onClick={emailPdf}
                disabled={emailing || emailSent}
                size="lg"
                variant="outline"
                className="rounded-full h-12 px-7 border-white/20 text-white hover:bg-white/10 bg-transparent"
                data-testid="congrats-email-btn"
              >
                {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : emailSent ? <>✓ Sendt til e-post</> : <><Mail className="h-4 w-4 mr-2" /> Send kopi til e-post</>}
              </Button>
              <Button
                asChild
                size="lg"
                variant="ghost"
                className="rounded-full h-12 px-7 text-white hover:bg-white/10"
                data-testid="congrats-open-plan"
              >
                <Link to={`/plan/${id}`}>Åpne <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-12">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Hva vil du gjøre nå?</p>
              <h2 className="mt-2 text-2xl sm:text-3xl font-medium tracking-tight text-slate-900">Kom i gang som gründer</h2>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-500">{doneCount}/{total} fullført</div>
              <div className="mt-1 w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                  data-testid="onboarding-progress"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {onboarding.steps.map((s) => (
              <Card key={s.key} className={`bg-white border border-gray-200 rounded-2xl transition-all ${s.done ? "opacity-70" : ""}`} data-testid={`onb-card-${s.key}`}>
                <CardContent className="p-5 flex items-center gap-4">
                  <Checkbox
                    checked={s.done}
                    onCheckedChange={(v) => toggleStep(s.key, !!v)}
                    className="h-5 w-5"
                    data-testid={`onb-check-${s.key}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-slate-900 font-medium ${s.done ? "line-through text-slate-500" : ""}`}>{s.label}</p>
                  </div>
                  {s.url && (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                      data-testid={`onb-link-${s.key}`}
                    >
                      Åpne <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
